<script lang="ts" setup>
import { ref, Ref, computed, onBeforeMount, onBeforeUnmount, inject } from 'vue';
import { useEvents } from '@/plugins/events';
import properties from '@/components/forms/properties';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { instanceOfUrlEntry, UrlType, UrlEntry } from '.';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';

defineOptions({
    name: 'url-blueprint',
    components: {
        ...properties
    }
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $events } = useEvents();
const blueprint = computed(() => props.blueprint);
const entryData = ref(new UrlEntry()) as Ref<UrlEntry>;
const level = inject<number>('level', 0);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfUrlEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();
    }
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        return value === null || readonly.value
            ? props.form.expressions.executeExpression(props.blueprint.defaultValue)
            : value;
    },
    set(value: string)
    {
        entryData.value.data = value;
    }
});

const readonly = computed(() => props.form.expressions.readonly(props.blueprint));
const required = computed(() => props.form.expressions.required(props.blueprint));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));
</script>

<template>
    <form-component-wrapper class="url-component" :form="form" :parent="parent" :blueprint="blueprint">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <ideo-input-group :prepend="blueprint.affix.prepend" :append="blueprint.affix.append">
                    <input type="url" class="form-control" v-model="value" :id="blueprint.id" :placeholder="placeholder" :readonly="readonly">
                </ideo-input-group>
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <ideo-form-localize v-slot="{ locale }">
                <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
            </ideo-form-localize>
            <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
            <field-condition v-model="blueprint.defaultValue" :form="form" :label="$t('[[[Wartość domyślna]]]')" type="text" />
            <ideo-form-localize v-slot="{ locale }">
                <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
            </ideo-form-localize>
            <field-affix v-model="blueprint.affix" />
            <ideo-form-localize v-slot="{ locale }">
                <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
            </ideo-form-localize>
            <field-visible :form="form" :blueprint="blueprint" />
            <field-readonly :form="form" :blueprint="blueprint" />
            <field-required :form="form" :blueprint="blueprint" />
            <field-error :form="form" :blueprint="blueprint" />
        </template>
    </form-component-wrapper>
</template>
